import { useState, useCallback } from "react";
import LogoAdoraTeam from "./LogoAdoraTeam";
import { auth, salvarToken } from "../Api";

const GOLD = "#F9A826";
const BLUE = "#0f1729";

const TIPOS = [
    { id: "lider",  emoji: "👑", label: "Líder",   desc: "Crio e administro um ministério" },
    { id: "musico", emoji: "🎸", label: "Músico",  desc: "Quero entrar na rede de músicos" },
];

// ✅ Input FORA do componente pai — evita recriar o elemento a cada render
// ✅ fontSize 16 — evita zoom automático no iOS
const Input = ({ label, value, onChange, type = "text", placeholder, autoComplete, inputMode, suffix }) => (
    <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#8899bb", marginBottom: 6 }}>{label}</div>
        <div style={{ position: "relative" }}>
            <input
                type={type}
                value={value}
                onChange={e => onChange(e.target.value)}
                placeholder={placeholder}
                autoComplete={autoComplete}
                inputMode={inputMode}
                style={{
                    width: "100%",
                    padding: suffix ? "13px 48px 13px 16px" : "13px 16px",
                    borderRadius: 12, border: "1.5px solid #2a3a5e",
                    background: "#0f1729", color: "white", fontSize: 16,
                    boxSizing: "border-box"
                }}
            />
            {suffix}
        </div>
    </div>
);

export default function Cadastro({ onLogin, onVoltar, onEscolherPlano }) {
    const [passo, setPasso] = useState(1);
    const [tipo, setTipo] = useState("");
    const [dados, setDados] = useState({
        nome: "", email: "", senha: "", confirmarSenha: "",
        dataNascimento: "", telefone: "", igreja: "", endereco: ""
    });
    const [erro, setErro] = useState("");
    const [carregando, setCarregando] = useState(false);
    const [verSenha, setVerSenha] = useState(false);

    // ✅ useCallback — funções estáveis evitam re-render dos inputs no mobile
    const set = useCallback((campo, valor) => setDados(p => ({ ...p, [campo]: valor })), []);
    const setNome      = useCallback(v => set("nome", v),            [set]);
    const setEmail     = useCallback(v => set("email", v),           [set]);
    const setDataNasc  = useCallback(v => set("dataNascimento", v),  [set]);
    const setTelefone  = useCallback(v => set("telefone", v),        [set]);
    const setSenha     = useCallback(v => set("senha", v),           [set]);
    const setConfirmar = useCallback(v => set("confirmarSenha", v),  [set]);
    const setIgreja    = useCallback(v => set("igreja", v),          [set]);
    const setEndereco  = useCallback(v => set("endereco", v),        [set]);

    const proximoPasso = () => {
        if (passo === 1) {
            if (!tipo) return setErro("Escolha o tipo de conta.");
            setErro("");
            setPasso(2);
        } else if (passo === 2) {
            if (!dados.nome || !dados.email || !dados.senha) return setErro("Preencha nome, e-mail e senha.");
            if (dados.senha.length < 6) return setErro("A senha precisa ter pelo menos 6 caracteres.");
            if (dados.senha !== dados.confirmarSenha) return setErro("As senhas não coincidem.");
            if (tipo === "lider") {
                setErro("");
                setPasso(3);
            } else {
                cadastrar();
            }
        } else if (passo === 3) {
            cadastrar();
        }
    };

    const cadastrar = async () => {
        setCarregando(true);
        setErro("");
        try {
            const res = await auth.cadastro({
                nome: dados.nome,
                email: dados.email,
                senha: dados.senha,
                data_nascimento: dados.dataNascimento,
                role: tipo,
                telefone: dados.telefone,
                igreja: dados.igreja,
                endereco: dados.endereco,
            });
            salvarToken(res.token);
            if (tipo === "lider") {
                onEscolherPlano(res.usuario);
            } else {
                onLogin(res.usuario);
            }
        } catch (e) {
            if (e.message === "Failed to fetch" || e.message.includes("fetch")) {
                setErro("Sem conexão com o servidor. Verifique sua internet e tente novamente.");
            } else {
                setErro(e.message || "Erro ao criar conta.");
            }
        } finally {
            setCarregando(false);
        }
    };

    return (
        <div style={{
            minHeight: "100vh", background: BLUE,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            padding: "24px 20px", boxSizing: "border-box"
        }}>
            <style>{`@keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }`}</style>

            <div style={{ animation: "fadeUp 0.4s ease both", marginBottom: 24, textAlign: "center" }}>
                <LogoAdoraTeam size={52} glow={true} />
                <div style={{ marginTop: 10 }}>
                    <span style={{ fontSize: 22, fontWeight: 900, color: "white" }}>Adora</span>
                    <span style={{ fontSize: 22, fontWeight: 900, color: GOLD }}>Team</span>
                </div>
            </div>

            <div style={{ display: "flex", gap: 8, marginBottom: 24, animation: "fadeUp 0.4s 0.05s ease both" }}>
                {[1, 2, tipo === "lider" ? 3 : null].filter(Boolean).map(n => (
                    <div key={n} style={{
                        width: passo === n ? 28 : 8, height: 8, borderRadius: 4,
                        background: passo >= n ? GOLD : "#2a3a5e",
                        transition: "all 0.3s"
                    }} />
                ))}
            </div>

            <div style={{
                animation: "fadeUp 0.4s 0.1s ease both",
                background: "#162040", borderRadius: 24,
                padding: 28, width: "100%", maxWidth: 400,
                boxSizing: "border-box", boxShadow: "0 8px 40px #0006"
            }}>

                {/* PASSO 1 — Tipo de conta */}
                {passo === 1 && (
                    <>
                        <div style={{ fontSize: 20, fontWeight: 800, color: "white", marginBottom: 4 }}>Criar conta 🎵</div>
                        <div style={{ fontSize: 13, color: "#8899bb", marginBottom: 24 }}>Como você vai usar o AdoraTeam?</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 24 }}>
                            {TIPOS.map(t => (
                                <button key={t.id} onClick={() => setTipo(t.id)} style={{
                                    display: "flex", alignItems: "center", gap: 16,
                                    padding: "16px 18px", borderRadius: 14,
                                    border: `2px solid ${tipo === t.id ? GOLD : "#2a3a5e"}`,
                                    background: tipo === t.id ? `${GOLD}15` : "#0f1729",
                                    cursor: "pointer", textAlign: "left", transition: "all 0.2s"
                                }}>
                                    <span style={{ fontSize: 32 }}>{t.emoji}</span>
                                    <div>
                                        <div style={{ fontSize: 16, fontWeight: 700, color: tipo === t.id ? GOLD : "white" }}>{t.label}</div>
                                        <div style={{ fontSize: 12, color: "#8899bb", marginTop: 2 }}>{t.desc}</div>
                                    </div>
                                    {tipo === t.id && <span style={{ marginLeft: "auto", color: GOLD, fontSize: 20 }}>✓</span>}
                                </button>
                            ))}
                        </div>
                    </>
                )}

                {/* PASSO 2 — Dados pessoais */}
                {passo === 2 && (
                    <>
                        <div style={{ fontSize: 20, fontWeight: 800, color: "white", marginBottom: 4 }}>Seus dados 📋</div>
                        <div style={{ fontSize: 13, color: "#8899bb", marginBottom: 20 }}>Preencha suas informações pessoais</div>

                        <Input label="NOME COMPLETO"       value={dados.nome}           onChange={setNome}      placeholder="Seu nome completo"  autoComplete="name"    inputMode="text" />
                        <Input label="E-MAIL"              value={dados.email}          onChange={setEmail}     type="email" placeholder="seu@email.com" autoComplete="email"   inputMode="email" />
                        <Input label="DATA DE NASCIMENTO"  value={dados.dataNascimento} onChange={setDataNasc}  type="date"                        autoComplete="bday" />
                        <Input label="TELEFONE / WHATSAPP" value={dados.telefone}       onChange={setTelefone}  placeholder="(47) 99999-9999"     autoComplete="tel"     inputMode="tel" />

                        <div style={{ marginBottom: 14 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#8899bb", marginBottom: 6 }}>SENHA</div>
                            <div style={{ position: "relative" }}>
                                <input
                                    type={verSenha ? "text" : "password"}
                                    value={dados.senha}
                                    onChange={e => setSenha(e.target.value)}
                                    placeholder="Mínimo 6 caracteres"
                                    autoComplete="new-password"
                                    style={{
                                        width: "100%", padding: "13px 48px 13px 16px", borderRadius: 12,
                                        border: "1.5px solid #2a3a5e", background: "#0f1729",
                                        color: "white", fontSize: 16, boxSizing: "border-box"
                                    }}
                                />
                                <button onClick={() => setVerSenha(p => !p)} style={{
                                    position: "absolute", right: 14, top: "50%",
                                    transform: "translateY(-50%)", background: "none",
                                    border: "none", cursor: "pointer", fontSize: 18, color: "#8899bb"
                                }}>{verSenha ? "🙈" : "👁"}</button>
                            </div>
                        </div>

                        <Input label="CONFIRMAR SENHA" value={dados.confirmarSenha} onChange={setConfirmar} type="password" placeholder="Repita a senha" autoComplete="new-password" />
                    </>
                )}

                {/* PASSO 3 — Dados da igreja (líder) */}
                {passo === 3 && (
                    <>
                        <div style={{ fontSize: 20, fontWeight: 800, color: "white", marginBottom: 4 }}>Sua Igreja ⛪</div>
                        <div style={{ fontSize: 13, color: "#8899bb", marginBottom: 20 }}>Informe os dados do seu ministério</div>
                        <Input label="NOME DA IGREJA" value={dados.igreja}   onChange={setIgreja}   placeholder="Ex: Igreja Batista Central" autoComplete="organization" />
                        <Input label="ENDEREÇO"        value={dados.endereco} onChange={setEndereco} placeholder="Cidade, Estado"             autoComplete="address-level2" inputMode="text" />
                        <div style={{
                            background: `${GOLD}10`, border: `1px solid ${GOLD}33`,
                            borderRadius: 12, padding: "12px 14px", marginBottom: 16
                        }}>
                            <div style={{ fontSize: 12, color: GOLD, fontWeight: 700, marginBottom: 4 }}>📌 Próximo passo</div>
                            <div style={{ fontSize: 12, color: "#8899bb" }}>Após o cadastro, você escolherá o plano ideal para o seu ministério.</div>
                        </div>
                    </>
                )}

                {erro && (
                    <div style={{
                        background: "#f4433618", border: "1px solid #f4433644",
                        borderRadius: 10, padding: "10px 14px", marginBottom: 16,
                        fontSize: 13, color: "#ff6b6b"
                    }}>⚠️ {erro}</div>
                )}

                <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                    <button onClick={passo === 1 ? onVoltar : () => setPasso(p => p - 1)} style={{
                        flex: 1, padding: 14, borderRadius: 14,
                        background: "none", border: "1.5px solid #2a3a5e",
                        color: "white", fontWeight: 600, fontSize: 14, cursor: "pointer"
                    }}>← Voltar</button>
                    <button onClick={proximoPasso} disabled={carregando} style={{
                        flex: 2, padding: 14, borderRadius: 14,
                        background: carregando ? "#2a3a5e" : `linear-gradient(135deg, ${GOLD}, #FFD166)`,
                        border: "none", color: BLUE, fontWeight: 800, fontSize: 15,
                        cursor: carregando ? "default" : "pointer",
                        boxShadow: carregando ? "none" : `0 4px 20px ${GOLD}44`
                    }}>
                        {carregando ? "Criando conta..." : passo === (tipo === "lider" ? 3 : 2) ? "Criar conta ✓" : "Continuar →"}
                    </button>
                </div>
            </div>
        </div>
    );
}